import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SeoHead from "@/components/feature/SeoHead";

const PROFILE_IMAGE = "https://static.readdy.ai/image/98c8f721efe8e85064ec351cb642a7fc/fcf7ee1e292dac4ebe06c44c77cd195d.png";

const venues = [
  "Berghain | Panorama Bar",
  "Tresor",
  "Nowadays — New York",
  "Dekmantel Selectors",
  "Wonderfruit Festival",
];

const DISKONNECTED_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Diskonnected",
  jobTitle: "DJ / Producer",
  description:
    "Diskonnected is a key figure in Taiwan's underground music scene and a driving force behind Organik Festival and the Smoke Machine collective. Resident Advisor described him as 'one of the world's greatest techno DJs'.",
  homeLocation: {
    "@type": "Place",
    name: "Taipei, Taiwan",
    geo: { "@type": "GeoCoordinates", latitude: 25.033, longitude: 121.5654 },
  },
  url: `${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/artist/diskonnected`,
  sameAs: [
    "https://soundcloud.com/diskonnected",
    "https://www.instagram.com/diskonnected_tw/",
  ],
  image: "https://static.readdy.ai/image/98c8f721efe8e85064ec351cb642a7fc/fcf7ee1e292dac4ebe06c44c77cd195d.png",
};

export default function ArtistPage() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  /* ── DNA Helix Canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      /* Faint grid */
      const COLS = 16;
      const ROWS = 20;
      for (let c = 0; c <= COLS; c++) {
        const x = (c / COLS) * W;
        const pulse = Math.sin(t * 0.01 + c * 0.35) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
        ctx.strokeStyle = `rgba(255,220,140,${0.012 + pulse * 0.015})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      for (let r = 0; r <= ROWS; r++) {
        const y = (r / ROWS) * H;
        const pulse = Math.sin(t * 0.008 + r * 0.28) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.strokeStyle = `rgba(255,220,140,${0.008 + pulse * 0.012})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      /* DNA Helix — right side */
      const helixX = W * 0.88;
      const helixAmplitude = 28;
      const helixStep = 18;
      const helixCount = Math.ceil(H / helixStep) + 2;

      for (let i = 0; i < helixCount; i++) {
        const y = i * helixStep - (t * 0.4 % helixStep);
        const phase = (i / helixCount) * Math.PI * 8 + t * 0.018;
        const x1 = helixX + Math.sin(phase) * helixAmplitude;
        const x2 = helixX + Math.sin(phase + Math.PI) * helixAmplitude;
        const alpha = Math.abs(Math.sin(phase)) * 0.35 + 0.05;

        /* strand 1 */
        if (i > 0) {
          const prevY = (i - 1) * helixStep - (t * 0.4 % helixStep);
          const prevPhase = ((i - 1) / helixCount) * Math.PI * 8 + t * 0.018;
          const prevX1 = helixX + Math.sin(prevPhase) * helixAmplitude;
          ctx.beginPath();
          ctx.moveTo(prevX1, prevY);
          ctx.lineTo(x1, y);
          ctx.strokeStyle = `rgba(255,220,140,${alpha * 0.7})`;
          ctx.lineWidth = 1;
          ctx.stroke();

          const prevX2 = helixX + Math.sin(prevPhase + Math.PI) * helixAmplitude;
          ctx.beginPath();
          ctx.moveTo(prevX2, prevY);
          ctx.lineTo(x2, y);
          ctx.strokeStyle = `rgba(255,200,100,${alpha * 0.5})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }

        /* connector */
        if (i % 3 === 0) {
          ctx.beginPath();
          ctx.moveTo(x1, y);
          ctx.lineTo(x2, y);
          ctx.strokeStyle = `rgba(255,220,140,${alpha * 0.25})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();

          /* node */
          const grd = ctx.createRadialGradient(x1, y, 0, x1, y, 4);
          grd.addColorStop(0, `rgba(255,220,140,${alpha * 0.6})`);
          grd.addColorStop(1, "rgba(255,200,100,0)");
          ctx.beginPath();
          ctx.arc(x1, y, 4, 0, Math.PI * 2);
          ctx.fillStyle = grd;
          ctx.fill();
        }
      }

      /* Fractal rings — top left */
      const cx = W * 0.08;
      const cy = H * 0.12;
      for (let r = 0; r < 5; r++) {
        const radius = 30 + r * 22 + Math.sin(t * 0.012 + r) * 4;
        const alpha = (0.06 - r * 0.01) * (Math.sin(t * 0.015 + r * 0.8) * 0.5 + 0.5);
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      /* Scan line */
      const scanY = ((t * 0.5) % H);
      const scanGrad = ctx.createLinearGradient(0, scanY - 60, 0, scanY + 60);
      scanGrad.addColorStop(0, "rgba(255,220,140,0)");
      scanGrad.addColorStop(0.5, "rgba(255,220,140,0.025)");
      scanGrad.addColorStop(1, "rgba(255,220,140,0)");
      ctx.fillStyle = scanGrad;
      ctx.fillRect(0, scanY - 60, W, 120);

      t++;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  /* ── Scroll reveal ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".reveal");
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = "1";
                (item as HTMLElement).style.transform = "translateY(0)";
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="relative min-h-screen w-full overflow-x-hidden"
      style={{ background: "#060606", cursor: "default" }}
    >
      <SeoHead
        title="Diskonnected | Artist Profile | MAVERICK vol.5"
        description="Diskonnectedのアーティストプロフィール。台湾のアンダーグラウンドテクノシーンの中心人物。Organik Festival、Smoke Machine主宰。Resident Advisorが「世界最高のテクノDJの一人」と評する。"
        keywords="Diskonnected, Taiwan techno DJ, Organik Festival, Smoke Machine, underground techno"
        canonical={`${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/artist/diskonnected`}
        ogImage="https://static.readdy.ai/image/98c8f721efe8e85064ec351cb642a7fc/fcf7ee1e292dac4ebe06c44c77cd195d.png"
        jsonLd={DISKONNECTED_JSONLD}
      />
      {/* Canvas background */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 w-full h-full pointer-events-none z-0"
      />

      {/* Back button */}
      <button
        onClick={() => navigate("/")}
        className="fixed top-7 left-7 md:left-12 z-50 flex items-center gap-3 group cursor-pointer whitespace-nowrap"
        style={{ fontFamily: "'Courier New', monospace" }}
      >
        <div
          className="w-8 h-8 flex items-center justify-center border rounded-full"
          style={{
            borderColor: "rgba(255,220,140,0.2)",
            transition: "border-color 0.3s ease",
          }}
        >
          <i
            className="ri-arrow-left-line"
            style={{ color: "rgba(255,220,140,0.5)", fontSize: "14px" }}
          />
        </div>
        <span
          className="text-[10px] tracking-[0.35em] uppercase hidden md:block"
          style={{ color: "rgba(255,220,140,0.3)", transition: "color 0.3s ease" }}
        >
          Back
        </span>
      </button>

      {/* Main content */}
      <div ref={sectionRef} className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-20 pt-24 md:pt-32 pb-32">

        {/* ── HERO BLOCK ── */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start mb-24 md:mb-36">

          {/* Profile image */}
          <div
            className="reveal flex-shrink-0 relative w-full lg:w-auto"
            style={{
              opacity: 0,
              transform: "translateY(24px)",
              transition: "opacity 1.2s ease, transform 1.2s ease",
            }}
          >
            {/* Decorative frame */}
            <div
              className="absolute -inset-3 pointer-events-none"
              style={{
                border: "1px solid rgba(255,220,140,0.08)",
                borderRadius: "2px",
              }}
            />
            <div
              className="absolute -top-1 -left-1 w-6 h-6 pointer-events-none"
              style={{ borderTop: "1px solid rgba(255,220,140,0.35)", borderLeft: "1px solid rgba(255,220,140,0.35)" }}
            />
            <div
              className="absolute -bottom-1 -right-1 w-6 h-6 pointer-events-none"
              style={{ borderBottom: "1px solid rgba(255,220,140,0.35)", borderRight: "1px solid rgba(255,220,140,0.35)" }}
            />

            <div
              className="overflow-hidden w-full lg:w-auto"
              style={{ height: "clamp(260px, 55vw, 480px)", maxWidth: "360px" }}
            >
              <img
                src={PROFILE_IMAGE}
                alt="Diskonnected"
                className="w-full h-full object-cover object-top"
                style={{ filter: "grayscale(15%) contrast(1.05)" }}
              />
            </div>

            {/* Coordinate label */}
            <div
              className="flex items-center gap-2 mt-4"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              <div style={{ width: "12px", height: "1px", background: "rgba(255,220,140,0.25)" }} />
              <span className="text-[9px] tracking-[0.3em] uppercase" style={{ color: "rgba(255,220,140,0.2)" }}>
                TW — 25.0330° N
              </span>
            </div>
          </div>

          {/* Text block */}
          <div className="flex flex-col gap-8 flex-1 pt-2 lg:pt-6">
            {/* Label */}
            <div
              className="reveal flex items-center gap-4"
              style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              <div style={{ width: "20px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
              <span
                className="text-[10px] tracking-[0.5em] uppercase"
                style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}
              >
                Artist Profile
              </span>
            </div>

            {/* Name */}
            <div
              className="reveal"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              <h1
                className="font-light leading-none tracking-wider"
                style={{
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontSize: "clamp(36px, 6vw, 80px)",
                  fontWeight: 200,
                  color: "rgba(255,255,255,0.92)",
                  letterSpacing: "0.06em",
                  textShadow: "0 0 60px rgba(255,220,140,0.1)",
                }}
              >
                Diskonnected
              </h1>
              <div className="flex items-center gap-3 mt-3">
                <div
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background: "rgba(255,220,140,0.6)",
                    boxShadow: "0 0 10px 2px rgba(255,200,100,0.25)",
                  }}
                />
                <span
                  className="text-xs tracking-[0.3em] uppercase"
                  style={{ color: "rgba(255,220,140,0.35)", fontFamily: "'Courier New', monospace" }}
                >
                  Taipei, Taiwan
                </span>
              </div>
            </div>

            {/* Tags */}
            <div
              className="reveal flex flex-wrap gap-2"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              {["Techno", "Underground", "Organik Festival", "Smoke Machine", "Pawnshop"].map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-[10px] tracking-[0.25em] uppercase"
                  style={{
                    border: "1px solid rgba(255,220,140,0.12)",
                    color: "rgba(255,220,140,0.35)",
                    fontFamily: "'Courier New', monospace",
                    borderRadius: "1px",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Bio */}
            <div
              className="reveal"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              <p
                className="leading-relaxed"
                style={{
                  fontSize: "clamp(15px, 1.1vw, 17px)",
                  color: "rgba(255,255,255,0.5)",
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontWeight: 300,
                  letterSpacing: "0.02em",
                  lineHeight: "2.0",
                }}
              >
                Diskonnected is a key figure in Taiwan&apos;s underground music scene and a driving force
                behind Organik Festival and the Smoke Machine collective. Based in Taipei, he is closely
                affiliated with Pawnshop, where his approach to DJing continues to evolve.
              </p>
              <p
                className="leading-relaxed mt-5"
                style={{
                  fontSize: "clamp(15px, 1.1vw, 17px)",
                  color: "rgba(255,255,255,0.38)",
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontWeight: 300,
                  letterSpacing: "0.02em",
                  lineHeight: "2.0",
                }}
              >
                His sets — once described by Resident Advisor as those of &ldquo;one of the world&apos;s greatest
                techno DJs&rdquo; — balance intensity with subtlety, drawing from late-2000s dance music while
                staying firmly rooted in the present. Diskonnected delivers sets blending depth, drive,
                and fluidity across tempos.
              </p>
            </div>

            {/* Social links */}
            <div
              className="reveal flex items-center gap-6"
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
            >
              <a
                href="https://soundcloud.com/diskonnected"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-2 group cursor-pointer whitespace-nowrap"
                style={{ textDecoration: "none" }}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center border rounded-full"
                  style={{
                    borderColor: "rgba(255,220,140,0.18)",
                    transition: "border-color 0.3s ease, background 0.3s ease",
                  }}
                >
                  <i className="ri-soundcloud-line" style={{ color: "rgba(255,220,140,0.5)", fontSize: "14px" }} />
                </div>
                <span
                  className="text-[10px] tracking-[0.3em] uppercase"
                  style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}
                >
                  SoundCloud
                </span>
              </a>

              <a
                href="https://www.instagram.com/diskonnected_tw/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-2 group cursor-pointer whitespace-nowrap"
                style={{ textDecoration: "none" }}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center border rounded-full"
                  style={{
                    borderColor: "rgba(255,220,140,0.18)",
                    transition: "border-color 0.3s ease, background 0.3s ease",
                  }}
                >
                  <i className="ri-instagram-line" style={{ color: "rgba(255,220,140,0.5)", fontSize: "14px" }} />
                </div>
                <span
                  className="text-[10px] tracking-[0.3em] uppercase"
                  style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}
                >
                  Instagram
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div
          className="reveal w-full mb-24 md:mb-32"
          style={{
            opacity: 0,
            transform: "translateY(16px)",
            transition: "opacity 1.2s ease, transform 1.2s ease",
            height: "1px",
            background: "linear-gradient(to right, transparent, rgba(255,220,140,0.12), transparent)",
          }}
        />

        {/* ── VENUES ── */}
        <div className="mb-24 md:mb-32">
          <div
            className="reveal flex items-center gap-4 mb-12"
            style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
          >
            <div style={{ width: "20px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
            <span
              className="text-[10px] tracking-[0.5em] uppercase"
              style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}
            >
              Notable Venues
            </span>
          </div>

          <div className="flex flex-col">
            {venues.map((venue, i) => (
              <div
                key={i}
                className="reveal group relative py-5 border-b overflow-hidden"
                style={{
                  borderColor: "rgba(255,220,140,0.06)",
                  opacity: 0,
                  transform: "translateY(16px)",
                  transition: "opacity 1.2s ease, transform 1.2s ease",
                }}
              >
                {/* hover glow */}
                <div
                  className="absolute left-0 top-0 bottom-0 w-0 group-hover:w-full transition-all duration-700 pointer-events-none"
                  style={{ background: "linear-gradient(to right, rgba(255,220,140,0.03), transparent)" }}
                />
                <div className="flex items-center gap-5">
                  <span
                    className="text-white/[0.08]"
                    style={{ fontFamily: "'Courier New', monospace", fontSize: "11px", letterSpacing: "0.2em", flexShrink: 0 }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div
                    style={{
                      width: "4px",
                      height: "4px",
                      borderRadius: "50%",
                      background: "rgba(255,220,140,0.2)",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    className="font-light tracking-wider"
                    style={{
                      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                      fontSize: "clamp(14px, 1.8vw, 22px)",
                      fontWeight: 300,
                      color: "rgba(255,255,255,0.55)",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {venue}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── SOUNDCLOUD PLAYER ── */}
        <div className="mb-24 md:mb-32">
          <div
            className="reveal flex items-center gap-4 mb-10"
            style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
          >
            <div style={{ width: "20px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
            <span
              className="text-[10px] tracking-[0.5em] uppercase"
              style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}
            >
              Listen
            </span>
          </div>

          <div
            className="reveal relative"
            style={{
              opacity: 0,
              transform: "translateY(20px)",
              transition: "opacity 1.2s ease, transform 1.2s ease",
              border: "1px solid rgba(255,220,140,0.08)",
              padding: "2px",
            }}
          >
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-4 h-4 pointer-events-none" style={{ borderTop: "1px solid rgba(255,220,140,0.3)", borderLeft: "1px solid rgba(255,220,140,0.3)" }} />
            <div className="absolute top-0 right-0 w-4 h-4 pointer-events-none" style={{ borderTop: "1px solid rgba(255,220,140,0.3)", borderRight: "1px solid rgba(255,220,140,0.3)" }} />
            <div className="absolute bottom-0 left-0 w-4 h-4 pointer-events-none" style={{ borderBottom: "1px solid rgba(255,220,140,0.3)", borderLeft: "1px solid rgba(255,220,140,0.3)" }} />
            <div className="absolute bottom-0 right-0 w-4 h-4 pointer-events-none" style={{ borderBottom: "1px solid rgba(255,220,140,0.3)", borderRight: "1px solid rgba(255,220,140,0.3)" }} />

            <iframe
              title="Diskonnected on SoundCloud"
              width="100%"
              height="450"
              allow="autoplay"
              src="https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/diskonnected&color=%23ffdc8c&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=true&visual=true"
              style={{
                border: "none",
                display: "block",
                background: "#0a0a0a",
              }}
            />
          </div>

          {/* SoundCloud link */}
          <div
            className="reveal flex justify-end mt-4"
            style={{ opacity: 0, transform: "translateY(12px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
          >
            <a
              href="https://soundcloud.com/diskonnected"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-2 cursor-pointer whitespace-nowrap"
              style={{ textDecoration: "none" }}
            >
              <i className="ri-soundcloud-line" style={{ color: "rgba(255,220,140,0.3)", fontSize: "13px" }} />
              <span
                className="text-[9px] tracking-[0.35em] uppercase"
                style={{ color: "rgba(255,220,140,0.25)", fontFamily: "'Courier New', monospace" }}
              >
                View on SoundCloud
              </span>
              <i className="ri-external-link-line" style={{ color: "rgba(255,220,140,0.2)", fontSize: "10px" }} />
            </a>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div
          className="reveal w-full mb-24 md:mb-32"
          style={{
            opacity: 0,
            transform: "translateY(16px)",
            transition: "opacity 1.2s ease, transform 1.2s ease",
            height: "1px",
            background: "linear-gradient(to right, transparent, rgba(255,220,140,0.12), transparent)",
          }}
        />

        {/* ── RA QUOTE ── */}
        <div
          className="reveal relative mb-24 md:mb-32 px-5 md:px-16 py-10 md:py-16"
          style={{
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 1.2s ease, transform 1.2s ease",
            border: "1px solid rgba(255,220,140,0.07)",
          }}
        >
          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-5 h-5" style={{ borderTop: "1px solid rgba(255,220,140,0.3)", borderLeft: "1px solid rgba(255,220,140,0.3)" }} />
          <div className="absolute top-0 right-0 w-5 h-5" style={{ borderTop: "1px solid rgba(255,220,140,0.3)", borderRight: "1px solid rgba(255,220,140,0.3)" }} />
          <div className="absolute bottom-0 left-0 w-5 h-5" style={{ borderBottom: "1px solid rgba(255,220,140,0.3)", borderLeft: "1px solid rgba(255,220,140,0.3)" }} />
          <div className="absolute bottom-0 right-0 w-5 h-5" style={{ borderBottom: "1px solid rgba(255,220,140,0.3)", borderRight: "1px solid rgba(255,220,140,0.3)" }} />

          <p
            className="text-center font-light leading-relaxed mb-6"
            style={{
              fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
              fontSize: "clamp(16px, 2.2vw, 26px)",
              fontWeight: 200,
              color: "rgba(255,255,255,0.6)",
              letterSpacing: "0.04em",
              lineHeight: "1.7",
              fontStyle: "italic",
            }}
          >
            &ldquo;One of the world&apos;s greatest techno DJs&rdquo;
          </p>
          <p
            className="text-center text-[10px] tracking-[0.4em] uppercase"
            style={{ color: "rgba(255,220,140,0.3)", fontFamily: "'Courier New', monospace" }}
          >
            — Resident Advisor
          </p>
        </div>

        {/* ── BACK TO LINEUP ── */}
        <div
          className="reveal flex justify-center"
          style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 1.2s ease, transform 1.2s ease" }}
        >
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-4 px-8 py-4 cursor-pointer whitespace-nowrap"
            style={{
              border: "1px solid rgba(255,220,140,0.18)",
              fontFamily: "'Courier New', monospace",
              color: "rgba(255,220,140,0.4)",
              fontSize: "11px",
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              transition: "border-color 0.3s ease, color 0.3s ease",
              background: "transparent",
            }}
          >
            <i className="ri-arrow-left-line" style={{ fontSize: "12px" }} />
            Back to Lineup
          </button>
        </div>
      </div>
    </div>
  );
}
